/** Phase 17 — Onboarding i18n catalog.
 *  Single dictionary so every step can share vocabulary + we can lint that
 *  every backend step has both a `de` and `en` entry.
 */
export const ONBOARDING_STEPS = [
  "brand_profile", "regions", "target_groups", "products_services",
  "crm_viona", "ad_platform_meta", "ad_platform_google",
  "ad_platform_linkedin", "ad_platform_tiktok", "smtp", "autopilot_mode",
];

export const i18n = {
  de: {
    wizard_title: "Einrichtung",
    later: "Später fortsetzen",
    back: "Zurück",
    skip: "Überspringen",
    "continue": "Weiter",
    connect: "Verbinden",
    disconnect: "Trennen",
    reconnect: "Neu verbinden",
    save: "Speichern",
    testing: "Verbindung wird geprüft…",
    connected: "Verbunden",
    error: "Fehler",
    progress: "abgeschlossen",
    next_step: "Nächster Schritt",
    resume: "Einrichtung fortsetzen",
    dismiss: "Ausblenden",
    to_dashboard: "Zum Dashboard",
    provider_unavailable_title: "Verbindungsoptionen",
    provider_unavailable_body:
      "Du hast zwei Wege, dein Werbekonto anzubinden: die Plattform-App " +
      "(vom Betreiber bereitgestellt, ein Klick) oder deine eigene Meta / " +
      "Google / LinkedIn / TikTok App (empfohlen für den Test-Setup — kein " +
      "App-Review nötig). Wähle unten die Option, die zu dir passt.",
    provider_unavailable_support: "Support kontaktieren",
    docs: "Zur Dokumentation",
    intro_title: "Willkommen bei VIONA Marketing OS",
    intro_short: "Willkommen",
    intro_body:
      "In den nächsten Schritten richten wir dein Konto ein: Marke, Regionen, " +
      "Zielgruppen, Produkte, CRM und Werbekanäle. Du kannst jederzeit " +
      "abbrechen und später weitermachen.",
    intro_cta: "Los geht's",
    finish_title: "Bereit für den ersten Kampagnenstart",
    finish_short: "Fertig",
    finish_body: "Deine Grundeinrichtung ist abgeschlossen.",
    steps: {
      brand_profile: {t: "Markenprofil", s: "Name, Tonalität, Farben."},
      regions: {t: "Regionen", s: "Wo bewirbst du deine Angebote?"},
      target_groups: {t: "Zielgruppen", s: "Personas und Segmente."},
      products_services: {t: "Produkte & Services", s: "Was bietest du an?"},
      crm_viona: {t: "VIONA CRM", s: "Base-URL + API-Key eintragen."},
      ad_platform_meta: {t: "Meta (Facebook + Instagram)", s: "OAuth verbinden."},
      ad_platform_google: {t: "Google Ads", s: "OAuth verbinden."},
      ad_platform_linkedin: {t: "LinkedIn Ads", s: "OAuth verbinden."},
      ad_platform_tiktok: {t: "TikTok Ads", s: "OAuth verbinden."},
      smtp: {t: "SMTP", s: "Vom Operator per Env gesetzt."},
      autopilot_mode: {t: "Autopilot-Modus", s: "Wie autonom soll VIONA agieren?"},
    },
    autopilot: {
      prepare_only: {
        title: "Nur vorbereiten (empfohlen)",
        body: "VIONA bereitet Aktionen vor. Nichts geschieht automatisch."},
      with_approval: {
        title: "Mit Freigabe ausführen",
        body: "VIONA erstellt Entwürfe. Jede Änderung braucht dein OK."},
      auto_within_frame: {
        title: "Automatik im Rahmen",
        body: "VIONA führt kleine Änderungen im gesetzten Rahmen aus (mit Limits + Not-Aus)."},
    },
    warn_autopilot: "Empfehlung für Beta: bei 'Nur vorbereiten' bleiben.",
    viona: {
      base_url: "Base-URL",
      api_key: "API-Key",
      tenant_scope: "Tenant-Scope (optional)",
      test_ok: "Verbindung erfolgreich.",
      test_error: "Verbindung fehlgeschlagen: ",
      already_configured: "Aktuell verbunden mit ",
    },
    smtp_body_disabled:
      "SMTP ist derzeit deaktiviert. Der Operator muss folgende Env-Variablen setzen:",
    smtp_body_enabled: "SMTP ist aktiviert und einsatzbereit.",
    banner_title: "Setup zu {pct}% abgeschlossen",
  },
  en: {
    wizard_title: "Setup",
    later: "Continue later",
    back: "Back",
    skip: "Skip",
    "continue": "Continue",
    connect: "Connect",
    disconnect: "Disconnect",
    reconnect: "Reconnect",
    save: "Save",
    testing: "Testing connection…",
    connected: "Connected",
    error: "Error",
    progress: "complete",
    next_step: "Next step",
    resume: "Resume setup",
    dismiss: "Hide",
    to_dashboard: "Go to dashboard",
    provider_unavailable_title: "Connection options",
    provider_unavailable_body:
      "You have two ways to attach your ad account: the platform app " +
      "(operator-provided, one click) or your own Meta / Google / " +
      "LinkedIn / TikTok app (recommended for the test setup — no " +
      "App-Review needed). Pick the option that suits you below.",
    provider_unavailable_support: "Contact support",
    docs: "View docs",
    intro_title: "Welcome to VIONA Marketing OS",
    intro_short: "Welcome",
    intro_body:
      "The next steps will set up your account: brand, regions, target groups, " +
      "products, CRM and ad channels. You can leave anytime and pick up later.",
    intro_cta: "Let's go",
    finish_title: "Ready for your first campaign",
    finish_short: "Done",
    finish_body: "Your baseline setup is complete.",
    steps: {
      brand_profile: {t: "Brand profile", s: "Name, tone, colors."},
      regions: {t: "Regions", s: "Where do you advertise?"},
      target_groups: {t: "Target groups", s: "Personas and segments."},
      products_services: {t: "Products & services", s: "What do you offer?"},
      crm_viona: {t: "VIONA CRM", s: "Base URL + API key."},
      ad_platform_meta: {t: "Meta (Facebook + Instagram)", s: "Connect via OAuth."},
      ad_platform_google: {t: "Google Ads", s: "Connect via OAuth."},
      ad_platform_linkedin: {t: "LinkedIn Ads", s: "Connect via OAuth."},
      ad_platform_tiktok: {t: "TikTok Ads", s: "Connect via OAuth."},
      smtp: {t: "SMTP", s: "Operator-configured via env vars."},
      autopilot_mode: {t: "Autopilot mode", s: "How autonomous should VIONA be?"},
    },
    autopilot: {
      prepare_only: {
        title: "Prepare only (recommended)",
        body: "VIONA prepares actions. Nothing runs automatically."},
      with_approval: {
        title: "Execute with approval",
        body: "VIONA drafts changes. Every change waits for your OK."},
      auto_within_frame: {
        title: "Automation within frame",
        body: "VIONA executes small changes within your set frame (guardrails + kill-switch)."},
    },
    warn_autopilot: "Beta recommendation: stay on 'Prepare only'.",
    viona: {
      base_url: "Base URL",
      api_key: "API key",
      tenant_scope: "Tenant scope (optional)",
      test_ok: "Connection successful.",
      test_error: "Connection failed: ",
      already_configured: "Currently connected to ",
    },
    smtp_body_disabled:
      "SMTP is currently disabled. The operator must set the following env vars:",
    smtp_body_enabled: "SMTP is enabled and ready.",
    banner_title: "Setup {pct}% complete",
  },
};

export function useLang() {
  if (typeof window === "undefined") return "de";
  const stored = window.localStorage.getItem("viona:lang")
              || window.localStorage.getItem("viona.locale");
  if (stored === "de" || stored === "en") return stored;
  const nav = typeof navigator !== "undefined"
    ? (navigator.language || "de").slice(0, 2) : "de";
  // Phase 6 (Feb 2026) — default to German. VIONA is a German-first
  // product; falling back to English on an empty language preference
  // used to leak English UI onto German operators.
  return nav === "en" ? "en" : "de";
}

export function setLang(lang) {
  if (typeof window !== "undefined") {
    window.localStorage.setItem("viona:lang", lang);
    window.dispatchEvent(new CustomEvent("viona:lang-changed",
                                              {detail: {lang}}));
  }
}
